import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form } from 'react-bootstrap';
import { StarFill } from 'react-bootstrap-icons';
import { useToolExecution } from '@/hooks/useToolExecution';
import { ProgressTracker } from '@/components/tools/ProgressTracker';
import { GroupedClientSelector } from '@/components/tools/GroupedClientSelector';
import { countOf, reasonOf } from '@/components/tools/clientSelectionUtils';
import {
  IdCell,
  ReportChoices,
  ReportDownloads,
  ReportStats,
  ReportTable,
  type ReportChoice,
  type ReportColumn,
} from '@/components/tools/ReportParts';
import { ToolPage } from '@/components/ui/ToolPage';
import { clientService } from '@/services/clientService';
import { QuotaReportService } from '@/services/quotaReportService';
import { formatNzDateTime } from '@/utils/relativeTime';
import type { Client } from '@/types';
import { ALL_MODEL_FAMILIES, ALL_QUOTA_TYPES, ALL_QUOTA_METRICS } from '@numa/quota-snapshot';
import type {
  QuotaReportParameters,
  QuotaReportResult,
  ToolResult,
  ToolResultFile,
  QuotaDescriptor,
  QuotaReportRow,
  ModelFamily,
  QuotaMetric,
  QuotaType,
} from '@/types/tools';
import './QuotaReportTool.css';

type QuotaChoices = Omit<QuotaReportParameters, 'clientScope' | 'clients'>;

const DEFAULT_TYPES: QuotaType[] = [...ALL_QUOTA_TYPES];
const DEFAULT_FAMILIES: ModelFamily[] = [...ALL_MODEL_FAMILIES];
const DEFAULT_METRICS: QuotaMetric[] = [...ALL_QUOTA_METRICS];

const DEFAULT_CHOICES: QuotaChoices = {
  regionMode: 'client-region',
  modelFamilies: DEFAULT_FAMILIES,
  quotaMetrics: DEFAULT_METRICS,
  advancedFilter: '',
  types: DEFAULT_TYPES,
  output: 'table+csv',
};

const FAMILY_LABELS: Record<ModelFamily, string> = {
  sonnet: 'Sonnet',
  opus: 'Opus',
  haiku: 'Haiku',
  nova: 'Nova',
};

const METRIC_LABELS: Record<QuotaMetric, string> = {
  'requests-per-minute': 'Requests a minute',
  'tokens-per-minute': 'Tokens a minute',
  'requests-per-day': 'Requests a day',
  'tokens-per-day': 'Tokens a day',
};

const REGION_MODE_LABEL: Record<QuotaReportParameters['regionMode'], string> = {
  'client-region': "Each client's own region",
  'all-regions': 'Both regions',
};

/** How many client names the summary spells out before it folds the rest into a count. */
const NAMES_SHOWN = 8;

export default function QuotaReportTool() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [selectedClientNames, setSelectedClientNames] = useState<string[]>([]);
  const [useArcanumInternal, setUseArcanumInternal] = useState(false);
  const [resultFiles, setResultFiles] = useState<ToolResultFile[]>([]);
  const [resultQuotas, setResultQuotas] = useState<QuotaDescriptor[]>([]);
  const [resultRows, setResultRows] = useState<QuotaReportRow[]>([]);

  const [parameters, setParameters] = useState<QuotaChoices>(DEFAULT_CHOICES);

  const { execution, isRunning, execute, cancel, reset } = useToolExecution({
    onCompleted: (result) => {
      const data = result.data as QuotaReportResult | undefined;
      if (result.files) setResultFiles(result.files);
      if (data?.quotas) setResultQuotas(data.quotas);
      if (data?.rows) setResultRows(data.rows);
    },
    onFailed: (error) => {
      console.error('Tool execution failed:', error);
    },
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoadingClients(true);
    setClientsError(null);
    try {
      const allClients = await clientService.getAllClients();
      setClients(allClients);
    } catch (err) {
      console.error('Failed to load clients:', err);
      setClientsError(reasonOf(err));
    } finally {
      setLoadingClients(false);
    }
  };

  const handleParamChange = (patch: Partial<QuotaChoices>) => {
    setParameters((prev) => ({ ...prev, ...patch }));
  };

  const handleClientToggle = (clientName: string) => {
    setSelectedClientNames((prev) =>
      prev.includes(clientName) ? prev.filter((n) => n !== clientName) : [...prev, clientName]
    );
  };

  const handleSelectClients = (clientNames: string[]) => {
    setSelectedClientNames(clientNames);
  };

  /** Ticks or clears one value of a choice that takes several. */
  const toggleIn = <T,>(list: T[], value: T, on: boolean): T[] => {
    const next = new Set(list);
    if (on) next.add(value);
    else next.delete(value);
    return Array.from(next);
  };

  const allClientsChosen = selectedClientNames.length === clients.length && clients.length > 0;

  const buildExecutionParams = (): QuotaReportParameters => {
    if (useArcanumInternal) {
      return { ...parameters, clientScope: 'arcanum-internal', clients: [] };
    }
    if (allClientsChosen) {
      return { ...parameters, clientScope: 'all', clients: [] };
    }
    return { ...parameters, clientScope: 'selected', clients: selectedClientNames };
  };

  const clearResults = () => {
    setResultFiles([]);
    setResultQuotas([]);
    setResultRows([]);
  };

  const handleExecute = async () => {
    clearResults();
    const execParams = buildExecutionParams();
    await execute('quota-report', execParams as unknown as Record<string, unknown>, async (params, onProgress) => {
      const { result, files } = await QuotaReportService.generateReport(
        params as unknown as QuotaReportParameters,
        onProgress
      );
      return { type: 'file', files, data: result } as ToolResult;
    });
  };

  /** Back to the form with the same choices. */
  const handleChangeChoices = () => {
    reset();
    clearResults();
  };

  /** Back to the form with every choice reset. */
  const handleReset = () => {
    reset();
    clearResults();
    setSelectedClientNames([]);
    setUseArcanumInternal(false);
    setParameters(DEFAULT_CHOICES);
  };

  /** Why the report cannot run yet, or nothing when it can. */
  const missingChoice = (): string | null => {
    if (!useArcanumInternal && selectedClientNames.length === 0) {
      return "Pick at least one client, or read Arcanum's own accounts.";
    }
    if (!parameters.modelFamilies || parameters.modelFamilies.length === 0) return 'Pick at least one model family.';
    if (!parameters.quotaMetrics || parameters.quotaMetrics.length === 0) return 'Pick at least one limit.';
    if (!parameters.types || parameters.types.length === 0) return 'Pick at least one route.';
    if (!parameters.output) return 'Pick what to show.';
    return null;
  };
  const blocked = missingChoice();

  const report = execution?.result?.data as QuotaReportResult | undefined;
  const finished = execution?.status === 'completed';
  const priorityModels = useMemo(
    () =>
      resultQuotas
        .filter((q) => q.isPriority)
        .map((q) => q.Model)
        .filter((v, i, a) => a.indexOf(v) === i),
    [resultQuotas]
  );

  const columns: ReportColumn<QuotaReportRow>[] = useMemo(
    () => [
      {
        key: 'account',
        header: 'Account',
        kind: 'wrap',
        cell: (row) => (
          <>
            <span className="quota-account">{row.accountName}</span>
            {row.isDev && (
              <Badge bg="info" className="ms-2">
                Dev
              </Badge>
            )}
          </>
        ),
      },
      {
        key: 'accountId',
        header: 'Account ID',
        cell: (row) => <IdCell value={row.accountId} label="Copy account ID" />,
      },
      { key: 'region', header: 'Region', className: 'mono', cell: (row) => row.region },
      {
        key: 'bedrock',
        header: 'Bedrock from',
        title: 'The account whose Bedrock limits this one runs on',
        cell: (row) =>
          row.bedrockAccount ? (
            <IdCell value={row.bedrockAccount} label="Copy account ID" />
          ) : (
            <span className="text-muted">Its own</span>
          ),
      },
      ...resultQuotas.map(
        (quota): ReportColumn<QuotaReportRow> => ({
          key: quota.QuotaCode,
          kind: 'number',
          title: quota.QuotaName,
          className: quota.isPriority ? 'quota-col--key' : undefined,
          header: (
            <>
              {quota.isPriority && <StarFill className="quota-star" aria-label="Newer model" />}
              {quota.Model}
              <span className="quota-col__sub">
                {quota.Type}, {METRIC_LABELS[quota.Metric].toLowerCase()}
              </span>
            </>
          ),
          cell: (row) => {
            const value = row.values[quota.QuotaCode];
            return value === null || value === undefined ? null : value.toLocaleString();
          },
        })
      ),
    ],
    [resultQuotas]
  );

  const choices: ReportChoice[] = [
    {
      label: 'Accounts',
      value: useArcanumInternal
        ? "Arcanum's own accounts"
        : allClientsChosen
          ? `All clients (${clients.length})`
          : selectedClientNames.length <= NAMES_SHOWN
            ? selectedClientNames.join(', ')
            : `${selectedClientNames.slice(0, NAMES_SHOWN).join(', ')} and ${selectedClientNames.length - NAMES_SHOWN} more`,
    },
    { label: 'Regions', value: REGION_MODE_LABEL[parameters.regionMode] },
    { label: 'Model families', value: parameters.modelFamilies.map((f) => FAMILY_LABELS[f]).join(', ') },
    { label: 'Limits', value: parameters.quotaMetrics.map((m) => METRIC_LABELS[m]).join(', ') },
    { label: 'Routes', value: parameters.types.join(', ') },
    ...(parameters.advancedFilter ? [{ label: 'Model name contains', value: parameters.advancedFilter }] : []),
  ];

  return (
    <ToolPage title="Quota report" subtitle="List the Bedrock request limits for each client account and region.">
      {clientsError && (
        <Alert variant="danger">
          The client list did not load, so there is nothing to pick from. {clientsError}{' '}
          <Button variant="link" className="report-inline-link" onClick={loadClients}>
            Try again
          </Button>
        </Alert>
      )}

      {!execution && (
        <Card className="report-form">
          <Card.Body>
            <div className="report-form__grid">
              <div className="report-form__col">
                <h2 className="section-label">Accounts</h2>
                <GroupedClientSelector
                  clients={clients}
                  selectedClientNames={selectedClientNames}
                  onClientToggle={handleClientToggle}
                  onSelectClients={handleSelectClients}
                  disabled={isRunning || useArcanumInternal}
                  loading={loadingClients}
                />
                <Form.Group className="report-field quota-own-accounts">
                  <Form.Check
                    type="switch"
                    id="arcanum-internal-toggle"
                    label="Read Arcanum's own accounts instead"
                    checked={useArcanumInternal}
                    onChange={(e) => setUseArcanumInternal(e.target.checked)}
                    disabled={isRunning}
                  />
                  <Form.Text>
                    Every Arcanum dev and production account, in both regions. The client list above is not used.
                  </Form.Text>
                </Form.Group>
              </div>

              <div className="report-form__col">
                <h2 className="section-label">Limits to list</h2>
                <Form.Group className="report-field">
                  <Form.Label>Model families</Form.Label>
                  <div className="report-checks">
                    {(Object.keys(FAMILY_LABELS) as ModelFamily[]).map((family) => (
                      <Form.Check
                        key={family}
                        type="checkbox"
                        id={`quota-family-${family}`}
                        label={FAMILY_LABELS[family]}
                        checked={parameters.modelFamilies.includes(family)}
                        onChange={(e) =>
                          handleParamChange({
                            modelFamilies: toggleIn(parameters.modelFamilies, family, e.target.checked),
                          })
                        }
                        disabled={isRunning}
                      />
                    ))}
                  </div>
                </Form.Group>

                <Form.Group className="report-field">
                  <Form.Label>Limits</Form.Label>
                  <div className="report-checks">
                    {(Object.keys(METRIC_LABELS) as QuotaMetric[]).map((metric) => (
                      <Form.Check
                        key={metric}
                        type="checkbox"
                        id={`quota-metric-${metric}`}
                        label={METRIC_LABELS[metric]}
                        checked={parameters.quotaMetrics.includes(metric)}
                        onChange={(e) =>
                          handleParamChange({
                            quotaMetrics: toggleIn(parameters.quotaMetrics, metric, e.target.checked),
                          })
                        }
                        disabled={isRunning}
                      />
                    ))}
                  </div>
                </Form.Group>

                <Form.Group className="report-field">
                  <Form.Label>Routes</Form.Label>
                  <div className="report-checks">
                    {ALL_QUOTA_TYPES.map((type) => (
                      <Form.Check
                        key={type}
                        type="checkbox"
                        id={`quota-type-${type}`}
                        label={type}
                        checked={parameters.types.includes(type)}
                        onChange={(e) =>
                          handleParamChange({ types: toggleIn(parameters.types, type, e.target.checked) })
                        }
                        disabled={isRunning}
                      />
                    ))}
                  </div>
                  <Form.Text>
                    How a request reaches the model: in the account&apos;s own region, across regions, or across the
                    world. Each route has its own limit.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="report-field" controlId="quota-model-filter">
                  <Form.Label>Model name contains (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={parameters.advancedFilter || ''}
                    onChange={(e) => handleParamChange({ advancedFilter: e.target.value })}
                    placeholder="Sonnet 5"
                    disabled={isRunning}
                  />
                  <Form.Text>Lists only the limits whose name has this text in it.</Form.Text>
                </Form.Group>
              </div>

              <div className="report-form__col">
                <h2 className="section-label">Regions and result</h2>
                <Form.Group className="report-field" controlId="quota-region-mode">
                  <Form.Label>Regions</Form.Label>
                  <Form.Select
                    value={parameters.regionMode}
                    onChange={(e) =>
                      handleParamChange({ regionMode: e.target.value as 'client-region' | 'all-regions' })
                    }
                    disabled={isRunning}
                  >
                    <option value="client-region">{REGION_MODE_LABEL['client-region']}</option>
                    <option value="all-regions">Both regions (us-east-1 and ap-southeast-2)</option>
                  </Form.Select>
                  <Form.Text>A dev account is always read in both regions.</Form.Text>
                </Form.Group>

                <Form.Group className="report-field" controlId="quota-output">
                  <Form.Label>Show</Form.Label>
                  <Form.Select
                    value={parameters.output}
                    onChange={(e) => handleParamChange({ output: e.target.value as 'table+csv' | 'csv' })}
                    disabled={isRunning}
                  >
                    <option value="table+csv">A table here, and a CSV to download</option>
                    <option value="csv">A CSV to download only</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
          </Card.Body>
          <div className="report-form__foot">
            <p className="report-form__hint">
              {blocked ||
                (useArcanumInternal
                  ? 'Reads every Arcanum account in both regions. The result has one row for each account and region.'
                  : `Reads ${countOf(selectedClientNames.length, 'client')}. Dev stacks that share an account come back as one row for each region.`)}
            </p>
            <Button variant="primary" onClick={handleExecute} disabled={blocked !== null || isRunning}>
              Run report
            </Button>
          </div>
        </Card>
      )}

      {execution && (
        <Card className="report-run">
          <Card.Body>
            <ReportChoices
              running={isRunning}
              onChange={handleChangeChoices}
              onStartOver={handleReset}
              choices={choices}
            />
            <div className="report-run__progress">
              <ProgressTracker
                status={execution.status}
                progress={execution.progress}
                error={execution.error}
                startedAt={execution.startedAt}
                completedAt={execution.completedAt}
                onStop={cancel}
              />
            </div>
          </Card.Body>
        </Card>
      )}

      {finished && resultFiles.length === 0 && (
        <Alert variant="warning">
          {report && report.metadata.totalClients === 0
            ? 'None of the chosen clients was found, so there is nothing to list. Change the choices and pick the clients again.'
            : 'Bedrock has no limit that matches these choices, so there is nothing to list. Change the choices: tick more model families, limits or routes, or clear the model name.'}
        </Alert>
      )}

      {finished && report && resultFiles.length > 0 && (
        <>
          <p className="report-result__note">
            {countOf(resultRows.length, 'row')}, one for each account and region, from{' '}
            {countOf(report.metadata.processedAccounts, 'account')}. Read {formatNzDateTime(report.metadata.runAt)} New
            Zealand time.
          </p>

          <ReportStats
            stats={[
              { label: 'Accounts read', value: report.metadata.processedAccounts.toLocaleString() },
              { label: 'Rows, one for each account and region', value: resultRows.length.toLocaleString() },
              { label: 'Limits in each row', value: resultQuotas.length.toLocaleString() },
            ]}
          />

          <Card className="report-card">
            <Card.Header className="report-card__head">
              <h2 className="report-card__title">Limits by account</h2>
              <ReportDownloads files={resultFiles} />
            </Card.Header>
            {parameters.output === 'csv' ? (
              <p className="report-card__body-note quota-csv-only">
                You asked for the CSV only, so no table is drawn here. The download has every row.
              </p>
            ) : (
              <>
                {priorityModels.length > 0 && (
                  <p className="report-card__body-note">
                    <StarFill className="quota-star" aria-hidden="true" />A star marks a newer model, version 4.5 or
                    later: {priorityModels.join(', ')}. An empty cell means the account gave no value for that limit.
                  </p>
                )}
                <ReportTable
                  columns={columns}
                  rows={resultRows}
                  empty="No account returned a limit."
                  rowClassName={(row) => (row.isDev ? 'report-table__row--quiet' : undefined)}
                />
              </>
            )}
          </Card>
        </>
      )}
    </ToolPage>
  );
}
